import React from "react";

export default function AnalysisSnapshotContent({ analysis, compact = false }) {
  const phase0 = analysis?.globalContext || null;
  const phase0Status = phase0?.reason || "HTF context unavailable";
  const structure = analysis?.structure || null;
  const zoneState = analysis?.zoneState || null;
  const bosState = analysis?.bosState || null;
  const signalState = analysis?.signalState || null;
  const zoneOutcome = analysis?.zoneOutcome || null;
  const htfAlignment = analysis?.htfAlignment !== false;
  const htfConflictReason = analysis?.htfConflictReason || "";
  const lockedZone = bosState?.zone || null;
  const activeZone = lockedZone || zoneState || null;
  const zoneColor =
    activeZone?.type === "DEMAND"
      ? "#22c55e"
      : activeZone?.type === "SUPPLY"
      ? "#ef4444"
      : "#e5e7eb";
  const tone = {
    BULLISH: "#22c55e",
    BEARISH: "#ef4444",
  };
  const phase0Tone = getContextTone(phase0);
  const displayConfidence =
    typeof analysis?.effectiveConfidence === "number"
      ? analysis.effectiveConfidence
      : phase0?.biasConfidence;
  const strengthProfile = getStrengthProfile(phase0?.biasConfidence ?? phase0?.strength);
  const structureLevel =
    typeof structure?.bosPrice === "number" ? structure.bosPrice.toFixed(2) : "--";
  const structureSummary = structure?.reason || "Waiting for structure";
  const signalSummary = signalState?.reason || "Waiting for trade signal";
  const zoneOutcomeTone = getZoneOutcomeTone(zoneOutcome?.state);
  const zoneOutcomeResult = zoneOutcome?.result?.result || "OPEN";
  const zoneOutcomeLastEvent =
    zoneOutcome?.lastEvent?.message || zoneOutcome?.lastEvent?.eventType || "--";
  const zoneOutcomePersistence =
    zoneOutcome?.result?.persistenceStatus || (zoneOutcome?.result ? "pending" : "--");
  const workflowStage = getWorkflowStage({
    htfAlignment,
    bosState,
    signalState,
    zoneOutcome,
    activeZone,
  });
  const zoneRangeLabel = formatRange(activeZone);
  const topContextNote = buildContextNote(phase0, phase0Status);

  return (
    <div style={styles.stack(compact)}>
      <section style={styles.sectionCard(compact)}>
        <div style={styles.overviewTopRow}>
          <div>
            <div style={styles.eyebrow}>HTF Context</div>
            <div style={{ ...styles.heroValue(compact), color: phase0Tone }}>
              {formatDisplayLabel(phase0?.globalBias)}
            </div>
          </div>
          <div style={styles.statusPill}>{strengthProfile.label}</div>
        </div>
        <div style={styles.summaryLine}>{strengthProfile.detail}</div>
        {compact ? (
          <div style={styles.metaLine}>{topContextNote}</div>
        ) : (
          <div style={styles.contextSummaryRow}>
            <div style={styles.metaLine}>{topContextNote}</div>
            <div style={styles.metaLineRight}>{phase0Status}</div>
          </div>
        )}
        <div style={styles.metricGrid(compact, true)}>
          <Metric label="Confidence" value={`${Math.round((displayConfidence || 0) * 100)}%`} />
          <Metric
            label="HTF Align"
            value={htfAlignment ? "Aligned" : "Optional"}
            color={htfAlignment ? "#22c55e" : "#f59e0b"}
          />
        </div>
      </section>

      <section style={styles.sectionCard(compact)}>
        <div style={styles.headerRow}>
          <div style={styles.sectionTitle}>Trade Workflow</div>
          <div style={styles.workflowBadge(workflowStage.color)}>{workflowStage.label}</div>
        </div>
        <div style={styles.metricGrid(compact)}>
          <Metric
            label="Structure"
            value={formatDisplayLabel(structure?.direction)}
            color={tone[structure?.direction] || "#e5e7eb"}
            bordered
          />
          <Metric
            label="BOS Lock"
            value={bosState?.state || "WAITING"}
            color={bosState?.state === "LOCKED" ? "#22c55e" : "#f59e0b"}
            bordered
          />
          <Metric label="Selected Zone" value={formatZoneLabel(activeZone)} color={zoneColor} bordered />
          <Metric
            label="Signal State"
            value={signalState?.state || "WAIT"}
            color={getSignalStateColor(signalState?.state)}
            bordered
          />
          <Metric label="BOS Level" value={structureLevel} bordered />
          <Metric label="Zone Range" value={zoneRangeLabel} bordered />
        </div>
        <div style={styles.textLine()}>{compact ? signalSummary : workflowStage.summary}</div>
        {!compact ? <div style={styles.textLine()}>{structureSummary}</div> : null}
        {!compact ? <div style={styles.textLine()}>{signalSummary}</div> : null}
        {!htfAlignment && htfConflictReason ? (
          <div style={styles.textLine("#fca5a5")}>{htfConflictReason}</div>
        ) : null}
      </section>

      <section style={styles.outcomeCard(zoneOutcomeTone, compact)}>
        <div style={styles.headerRow}>
          <div style={styles.sectionTitle}>Zone Outcome Tracker</div>
          <div style={styles.outcomeBadge(zoneOutcomeTone)}>{zoneOutcome?.state || "IDLE"}</div>
        </div>
        <div style={styles.metricGrid(compact)}>
          <Metric
            label="State"
            value={zoneOutcome?.state || "FRESH"}
            color={zoneOutcomeTone.accent}
            bordered
          />
          <Metric
            label="Outcome"
            value={formatDisplayLabel(zoneOutcomeResult)}
            color={zoneOutcomeTone.accent}
            bordered
          />
          <Metric
            label="Move Size"
            value={typeof zoneOutcome?.moveSize === "number" ? zoneOutcome.moveSize.toFixed(2) : "--"}
            bordered
          />
          <Metric label="Saved" value={formatDisplayLabel(zoneOutcomePersistence)} bordered />
        </div>
        <div style={styles.textLine(zoneOutcomeTone.reason)}>
          {compact ? zoneOutcome?.state || zoneOutcomeLastEvent : zoneOutcomeLastEvent}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, color = "#e5e7eb", bordered = false }) {
  return (
    <div style={styles.metricRow(bordered)}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={{ ...styles.metricValue, color }}>{value ?? "--"}</strong>
    </div>
  );
}

function buildContextNote(phase0, fallback) {
  if (phase0?.reason) {
    return phase0.reason;
  }

  return fallback || "--";
}

function getContextTone(phase0) {
  if (phase0?.globalBias === "UP_PRESSURE") {
    return "#22c55e";
  }

  if (phase0?.globalBias === "DOWN_PRESSURE") {
    return "#ef4444";
  }

  return "#cbd5e1";
}

function getStrengthProfile(strength) {
  if (typeof strength !== "number") {
    return {
      label: "--",
      detail: "Awaiting HTF context",
    };
  }

  if (strength >= 0.9) {
    return {
      label: "Extreme",
      detail: "Directional bias is strongly established",
    };
  }

  if (strength >= 0.75) {
    return {
      label: "Very Strong",
      detail: "High directional efficiency",
    };
  }

  if (strength >= 0.55) {
    return {
      label: "Strong",
      detail: "Clean higher-timeframe trend",
    };
  }

  if (strength >= 0.35) {
    return {
      label: "Moderate",
      detail: "Usable but mixed pressure",
    };
  }

  if (strength >= 0.2) {
    return {
      label: "Weak",
      detail: "Low directional commitment",
    };
  }

  return {
    label: "Very Weak",
    detail: "Choppy higher-timeframe flow",
  };
}

function formatDisplayLabel(value) {
  if (!value) {
    return "--";
  }

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getWorkflowStage({ htfAlignment, bosState, signalState, zoneOutcome, activeZone }) {
  if (!htfAlignment) {
    return {
      label: "HTF Mixed",
      color: "#f59e0b",
      summary: "Higher-timeframe direction conflicts with structure, so signal execution is paused.",
    };
  }

  if (bosState?.state !== "LOCKED") {
    return {
      label: "Waiting Lock",
      color: "#f59e0b",
      summary: "Structure exists, but BOS and narrative have not locked a tradeable zone yet.",
    };
  }

  if (!activeZone) {
    return {
      label: "No Zone",
      color: "#f59e0b",
      summary: "BOS is locked, but no active zone is available for outcome tracking.",
    };
  }

  if (signalState?.state === "SPIKE") {
    return {
      label: "Signal Ready",
      color: "#22c55e",
      summary: "A valid spike entry has been detected inside the selected zone.",
    };
  }

  if (signalState?.state === "ARMED") {
    return {
      label: "Signal Armed",
      color: "#38bdf8",
      summary: "Price is in the pre-zone buffer and approaching the execution area.",
    };
  }

  if (signalState?.state === "WATCH") {
    return {
      label: "Watching Zone",
      color: "#f59e0b",
      summary: "Price has touched the zone and the scanner is waiting for a valid spike candle.",
    };
  }

  if (signalState?.state === "FAIL") {
    return {
      label: "Signal Failed",
      color: "#ef4444",
      summary: "Price crossed beyond the allowed execution range for the selected zone.",
    };
  }

  if (zoneOutcome?.state === "ACTIVE") {
    return {
      label: "Zone Active",
      color: "#f59e0b",
      summary: "Price is inside the selected zone. Watch for spike respect or failure.",
    };
  }

  if (zoneOutcome?.state === "RESPECTED") {
    return {
      label: "Zone Respected",
      color: "#22c55e",
      summary: "The selected zone produced a spike reaction and the result was captured.",
    };
  }

  if (zoneOutcome?.state === "FAILED") {
    return {
      label: "Zone Failed",
      color: "#ef4444",
      summary: "Price broke through the selected zone buffer and failure was recorded.",
    };
  }

  if (zoneOutcome?.state === "APPROACHING") {
    return {
      label: "Approaching",
      color: "#f59e0b",
      summary: "The setup is valid and price is approaching the tracked zone.",
    };
  }

  return {
    label: "Ready",
    color: "#22c55e",
    summary: "BOS is locked and the system is waiting for price to interact with the selected zone.",
  };
}

function getSignalStateColor(state) {
  if (state === "SPIKE") {
    return "#22c55e";
  }

  if (state === "ARMED") {
    return "#38bdf8";
  }

  if (state === "WATCH") {
    return "#f59e0b";
  }

  if (state === "FAIL") {
    return "#ef4444";
  }

  return "#e5e7eb";
}

function getZoneOutcomeTone(state) {
  if (state === "RESPECTED") {
    return {
      accent: "#22c55e",
      border: "rgba(34, 197, 94, 0.34)",
      background:
        "radial-gradient(circle at top left, rgba(34, 197, 94, 0.16), rgba(15, 23, 42, 0.12) 58%), rgba(15, 23, 42, 0.76)",
      badgeBackground: "rgba(20, 83, 45, 0.34)",
      reason: "#d1fae5",
    };
  }

  if (state === "FAILED") {
    return {
      accent: "#ef4444",
      border: "rgba(239, 68, 68, 0.32)",
      background:
        "radial-gradient(circle at top left, rgba(239, 68, 68, 0.15), rgba(15, 23, 42, 0.12) 58%), rgba(15, 23, 42, 0.76)",
      badgeBackground: "rgba(127, 29, 29, 0.34)",
      reason: "#fee2e2",
    };
  }

  if (state === "ACTIVE" || state === "APPROACHING") {
    return {
      accent: "#f59e0b",
      border: "rgba(245, 158, 11, 0.28)",
      background:
        "radial-gradient(circle at top left, rgba(245, 158, 11, 0.16), rgba(15, 23, 42, 0.12) 58%), rgba(15, 23, 42, 0.76)",
      badgeBackground: "rgba(120, 53, 15, 0.34)",
      reason: "#fef3c7",
    };
  }

  return {
    accent: "#cbd5e1",
    border: "rgba(148, 163, 184, 0.2)",
    background:
      "radial-gradient(circle at top left, rgba(148, 163, 184, 0.12), rgba(15, 23, 42, 0.1) 58%), rgba(15, 23, 42, 0.72)",
    badgeBackground: "rgba(51, 65, 85, 0.48)",
    reason: "#cbd5e1",
  };
}

function formatZoneLabel(zone) {
  if (!zone) {
    return "--";
  }

  return zone.label || zone.name || zone.type || "--";
}

function formatRange(zone) {
  if (zone?.high == null || zone?.low == null) {
    return "--";
  }

  return `${zone.low.toFixed?.(2) ?? zone.low} - ${zone.high.toFixed?.(2) ?? zone.high}`;
}

const styles = {
  stack: (compact) => ({
    display: "grid",
    gap: compact ? 6 : 14,
  }),
  sectionCard: (compact) => ({
    display: "grid",
    gap: compact ? 6 : 12,
    padding: compact ? 8 : 16,
    borderRadius: compact ? 12 : 18,
    border: "1px solid rgba(58, 77, 112, 0.72)",
    background:
      "linear-gradient(180deg, rgba(17, 27, 49, 0.96), rgba(24, 34, 57, 0.94))",
    boxShadow: "inset 0 1px 0 rgba(148, 163, 184, 0.04)",
  }),
  outcomeCard: (tone, compact) => ({
    display: "grid",
    gap: compact ? 6 : 12,
    padding: compact ? 8 : 16,
    borderRadius: compact ? 12 : 18,
    border: "1px solid rgba(58, 77, 112, 0.72)",
    background: "linear-gradient(180deg, rgba(17, 27, 49, 0.96), rgba(24, 34, 57, 0.94))",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
  }),
  overviewTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  eyebrow: {
    fontSize: 10,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#59b7ff",
    fontWeight: 800,
  },
  heroValue: (compact) => ({
    marginTop: 4,
    fontSize: compact ? 13 : 22,
    fontWeight: 800,
    lineHeight: 1.1,
  }),
  statusPill: {
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(25, 36, 60, 0.95)",
    border: "1px solid rgba(75, 94, 131, 0.72)",
    color: "#dbe5f4",
    fontSize: 9,
    fontWeight: 800,
    whiteSpace: "nowrap",
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: "#59b7ff",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
  },
  workflowBadge: (color) => ({
    padding: "3px 8px",
    borderRadius: 999,
    background: "rgba(14, 31, 44, 0.92)",
    border: `1px solid ${color}cc`,
    color,
    fontSize: 9,
    fontWeight: 800,
    whiteSpace: "nowrap",
  }),
  outcomeBadge: (tone) => ({
    padding: "3px 8px",
    borderRadius: 999,
    background: "rgba(39, 50, 72, 0.92)",
    border: "1px solid rgba(75, 94, 131, 0.72)",
    color: "#dbe5f4",
    fontSize: 9,
    fontWeight: 800,
    whiteSpace: "nowrap",
    textTransform: "uppercase",
  }),
  summaryLine: {
    color: "#cfd9ea",
    fontSize: 10,
    lineHeight: 1.35,
  },
  metaLine: {
    color: "#b8c6db",
    fontSize: 9,
    lineHeight: 1.45,
  },
  metaLineRight: {
    color: "#b8c6db",
    fontSize: 9,
    lineHeight: 1.45,
    textAlign: "right",
  },
  contextSummaryRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(120px, auto)",
    gap: 8,
    alignItems: "center",
  },
  metricGrid: (compact, boxed = false) => ({
    display: "grid",
    gridTemplateColumns: compact ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
    gap: compact && boxed ? 6 : boxed ? 10 : 0,
    borderTop: boxed ? "none" : "1px solid rgba(57, 74, 104, 0.62)",
    borderBottom: boxed ? "none" : "1px solid rgba(57, 74, 104, 0.62)",
  }),
  metricRow: (bordered) => ({
    display: "grid",
    gap: 3,
    padding: bordered ? "6px 7px 7px" : "8px 7px",
    minHeight: bordered ? 46 : "auto",
    borderRadius: bordered ? 10 : 0,
    border: bordered ? "1px solid rgba(57, 74, 104, 0.72)" : "0 solid transparent",
    borderBottom: bordered ? "1px solid rgba(57, 74, 104, 0.72)" : "1px solid rgba(57, 74, 104, 0.62)",
    borderRight: bordered ? "1px solid rgba(57, 74, 104, 0.72)" : "1px solid rgba(57, 74, 104, 0.62)",
    background: bordered ? "transparent" : "transparent",
  }),
  metricLabel: {
    color: "#cfd9ea",
    fontSize: 10,
    fontWeight: 700,
  },
  metricValue: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  textLine: (color = "#b6c3d8") => ({
    color,
    fontSize: 9,
    lineHeight: 1.45,
    paddingTop: 2,
  }),
};
